import { TRASH_RETENTION_DAYS, type WorkspaceItem } from "@hydra-notes/shared";
import type { DataContext } from "./store";

type ContentKind = "note" | "task";

interface PendingDelete {
  workspaceId: string;
  contentKind: ContentKind;
  itemId: string;
  shared: boolean;
}

const PENDING_KEY = "hn.pending-content-deletes";
const RETENTION_MS = TRASH_RETENTION_DAYS * 24 * 60 * 60 * 1000;
const MAX_TIMEOUT_MS = 2_147_000_000;

function pendingDeletes(): PendingDelete[] {
  try {
    const value = JSON.parse(localStorage.getItem(PENDING_KEY) ?? "[]") as unknown;
    return Array.isArray(value) ? (value as PendingDelete[]) : [];
  } catch {
    return [];
  }
}

function savePending(items: PendingDelete[]): void {
  if (items.length) localStorage.setItem(PENDING_KEY, JSON.stringify(items));
  else localStorage.removeItem(PENDING_KEY);
}

function queueDelete(item: PendingDelete): void {
  const items = pendingDeletes();
  if (!items.some((x) => x.workspaceId === item.workspaceId && x.contentKind === item.contentKind && x.itemId === item.itemId)) {
    items.push(item);
    savePending(items);
  }
}

function contentKind(item: WorkspaceItem): ContentKind | null {
  if (item.type === "note" || item.type === "card") return "note";
  if (item.type === "tasklist") return "task";
  return null;
}

function deleteUrl(item: PendingDelete): string {
  const path = item.contentKind === "note" ? `/api/notes/${item.itemId}` : `/api/task-lists/${item.itemId}`;
  return item.shared ? `${path}?spaceId=${encodeURIComponent(item.workspaceId)}` : path;
}

async function requestDelete(item: PendingDelete): Promise<boolean> {
  try {
    const res = await fetch(deleteUrl(item), { method: "DELETE", credentials: "same-origin" });
    // A retry after successful cleanup is intentionally idempotent. A 404
    // also covers a shared space the user no longer owns.
    return res.ok || res.status === 404;
  } catch {
    return false;
  }
}

/** Retry privacy cleanup that was requested while this space was offline. */
export async function flushPendingDeletes(workspaceId: string): Promise<void> {
  const all = pendingDeletes();
  const keep: PendingDelete[] = [];
  for (const item of all) {
    if (item.workspaceId !== workspaceId || !(await requestDelete(item))) keep.push(item);
  }
  savePending(keep);
}

/**
 * Remove one tree item and all content docs in its subtree. The live tree is
 * the source of truth, while failed server byte cleanup is queued locally so
 * an offline permanent delete still finishes when this space reconnects.
 */
export async function deleteWorkspaceItemForever(ctx: DataContext, rootId: string): Promise<{ cloudPending: boolean }> {
  const before = new Map(ctx.workspace.getSnapshot().map((item) => [item.id, item]));
  const removedIds = ctx.workspace.deletePermanent(rootId);
  const removed = removedIds.flatMap((id) => {
    const item = before.get(id);
    return item ? [item] : [];
  });

  let cloudPending = false;
  for (const item of removed) {
    const kind = contentKind(item);
    if (kind === "note") await ctx.notes.destroyStorage(item.id);
    if (kind === "task") await ctx.tasks.destroyStorage(item.id);
    if (!kind || ctx.workspaceId === "local") continue;

    const pending: PendingDelete = {
      workspaceId: ctx.workspaceId,
      contentKind: kind,
      itemId: item.id,
      shared: ctx.kind === "shared",
    };
    if (!(await requestDelete(pending))) {
      queueDelete(pending);
      cloudPending = true;
    }
  }

  if (removedIds.length) {
    const [{ useTabs }, { useRecents }] = await Promise.all([import("@/store/tabs"), import("@/store/recents")]);
    useTabs.getState().closeItemEverywhere(removedIds);
    useRecents.getState().remove(removedIds);
  }
  return { cloudPending };
}

/** Purge only roots of subtrees that have spent the full retention in trash. */
export async function purgeExpiredTrash(ctx: DataContext): Promise<void> {
  const cutoff = Date.now() - RETENTION_MS;
  const items = ctx.workspace.getSnapshot();
  const byId = new Map(items.map((item) => [item.id, item]));
  const roots = items.filter((item) => {
    if (!item.trashedAt || item.trashedAt > cutoff) return false;
    const parent = item.parentId ? byId.get(item.parentId) : undefined;
    return !parent?.trashedAt || parent.trashedAt > cutoff;
  });
  for (const item of roots) await deleteWorkspaceItemForever(ctx, item.id);
}

/**
 * Keep trash retention exact while a workspace remains mounted. Workspace
 * changes reschedule the nearest deadline; long delays are clamped to the
 * maximum reliable browser timeout and checked again when that timer fires.
 */
export function watchTrashRetention(ctx: DataContext): () => void {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let stopped = false;
  let purging = false;

  const clearTimer = () => {
    if (timer) clearTimeout(timer);
    timer = null;
  };

  const schedule = () => {
    if (stopped || ctx.destroyed) return;
    if (purging) return;
    clearTimer();
    const expiries = ctx.workspace
      .getSnapshot()
      .flatMap((item) => (item.trashedAt ? [item.trashedAt + RETENTION_MS] : []));
    if (!expiries.length) return;
    const delay = Math.min(MAX_TIMEOUT_MS, Math.max(0, Math.min(...expiries) - Date.now()));
    timer = setTimeout(() => void purgeAndReschedule(), delay);
  };

  const purgeAndReschedule = async () => {
    if (stopped || ctx.destroyed || purging) return;
    timer = null;
    purging = true;
    try {
      await purgeExpiredTrash(ctx);
    } finally {
      purging = false;
      if (!stopped && !ctx.destroyed) schedule();
    }
  };

  const unsubscribe = ctx.workspace.subscribe(schedule);
  schedule();
  return () => {
    stopped = true;
    clearTimer();
    unsubscribe();
  };
}
